import React, { useState } from 'react';
import styled from 'styled-components';

import type { ICardTable, ICardTableRowWrapper, ICardTableRow } from '@/components/common/types';

import { media } from '@/styles/theme';
import { ChevronUp } from '@/assets/icons';

const CardTable = ({ children }: ICardTable): React.ReactElement => <Container>{children}</Container>;

const CardTableRowsWrapper = ({ children, isCollapsable = false }: ICardTableRowWrapper): React.ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  return (
    <div>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            collapsed: isCollapsed,
            setIsCollapsed: setIsCollapsed,
            isCollapsable: isCollapsable,
          });
        }
        return child;
      })}
    </div>
  );
};

const TableRow = ({ children, collapsed, setIsCollapsed, isCollapsable }: ICardTableRow): React.ReactElement => (
  <Row className={`${collapsed ? 'collapsed' : ''}`}>
    {children}
    {isCollapsable && (
      <ExpandRowButton
        className={`${collapsed ? 'collapsed' : ''}`}
        onClick={() => setIsCollapsed && setIsCollapsed(!collapsed)}
      />
    )}
  </Row>
);

const TableExpandedRow = ({ children, collapsed }: ICardTableRow): React.ReactElement => (
  <ExpandedRow className={`${collapsed ? 'collapsed' : ''}`}>{children}</ExpandedRow>
);

const Container = styled.div.attrs({
  className: 'mt-3',
})`
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: start;

  height: auto;
  padding: 15px 10px 0;

  background-color: #f3f4f6;
  border: 1px solid #f3f4f6;
  border-radius: 8px 8px 0 0;

  &:last-child {
    margin-bottom: 8px;
    padding-bottom: 15px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 15px 10px;

    &.collapsed {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      margin-bottom: 8px;
    }
  }
`;

const ExpandedRow = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: start;

  height: auto;

  padding: 0 10px 15px;

  background-color: #f3f4f6;
  border: 1px solid #f3f4f6;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  &:last-child {
    margin-bottom: 8px;
  }

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    background-color: #fff;
    padding: 15px 10px;

    &.collapsed {
      display: none;
    }
  }
`;

const Cell = styled.div`
  white-space: nowrap;
  color: #6b7280;

  padding-bottom: 10px;
  padding-left: 10px;
  &:last-child {
    padding-bottom: 0;
    padding-right: 35px;
  }

  ${media.tablet} {
    padding: 0 14px;

    &:last-child {
      padding-right: 35px;
    }
  }
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: #4b5563;
`;

const Body = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #374151;
`;

const ExpandRowButton = styled.div`
  display: none;

  ${media.tablet} {
    display: block;
    position: absolute;
    top: 10px;
    right: 15px;
    width: 34px;
    height: 34px;

    background-image: url(${ChevronUp});
    background-size: 15px;
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;

    transition: transform 0.3s ease-in-out;

    &.collapsed {
      transform: rotate(180deg);
    }
  }
`;

CardTable.Container = Container;
CardTable.CardTableRowsWrapper = CardTableRowsWrapper;
CardTable.TableRow = TableRow;
CardTable.TableExpandedRow = TableExpandedRow;
CardTable.Row = Row;
CardTable.Cell = Cell;
CardTable.Title = Title;
CardTable.Body = Body;

export { CardTable };
